import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import MaskedTextField from '@/common/components/MaskedTextField'
import TextField from '@/common/components/TextField'
import { customUnitMask } from '@/common/constants/masks.constant'
import { options as unitForQuantity } from '@/common/constants/unitForQuantityOptions'
import { ErrorResponse } from '@/common/types/errorResponse'
import { ProductData } from '@/common/types/product.type'
import { cn } from '@/common/utils/cn'
import { formatCurrency, formatDecimal, formatUnit } from '@/common/utils/formatters'
import { useCardCurrentLimits } from '@/features/cardCurrentLimits/hooks/useCardCurrentLimits'
import { CardDetailsData } from '@/features/cards/types/CardDetails.type'
import { AlertTriangleIcon, FileBoxIcon, InfoIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

interface CardCurrentLimitsProps {
  card?: string
  cardDetails?: CardDetailsData
  product: ProductData
  establishmentBranchId?: string
}

export const CardCurrentLimits = ({ card, cardDetails, product, establishmentBranchId }: CardCurrentLimitsProps) => {
  const [errorBase, setErrorBase] = useState<string[]>([])

  const {
    data: cardCurrentLimits,
    isPending: cardCurrentLimitsIsPending,
    isError: cardCurrentLimitsIsError,
    error: cardCurrentLimitsError,
  } = useCardCurrentLimits({
    id: card,
    product_id: product.id,
    establishment_branch_id: establishmentBranchId,
  })

  const showCardLimits = !cardCurrentLimitsIsPending && cardCurrentLimits
  const showLoading = cardCurrentLimitsIsPending && !cardCurrentLimitsIsError
  const showError = !cardCurrentLimitsIsPending && cardCurrentLimitsIsError

  const shouldHighlightCreditField = (fieldValue: string | null, creditLimit: string | null): string => {
    if (!fieldValue || !creditLimit) return ''

    return Number(fieldValue) <= Number(creditLimit) ? 'text-danger-soft [&>*]:text-danger-soft' : ''
  }

  const shouldHighlightQuantityField = (fieldValue: number | null, quantityLimit: number | null): string => {
    if (!fieldValue || !quantityLimit) return ''

    return fieldValue <= quantityLimit ? 'text-danger-soft [&>*]:text-danger-soft' : ''
  }

  useEffect(() => {
    if (cardCurrentLimitsIsError) {
      const errors = (cardCurrentLimitsError as ErrorResponse).response?.data?.errors?.base

      if (errors && errors?.length) {
        setErrorBase(errors)
      }
    }
  }, [cardCurrentLimitsIsError])

  return (
    <div key={product.id}>
      {showCardLimits && (
        <Box className="flex flex-col gap-2">
          <p>Valores liberados</p>

          <div className="flex gap-2">
            <div>
              <InfoIcon strokeWidth={1} color="var(--warning)" />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[12px]">
                Os valores exibidos são calculados conforme todas as limitações e créditos garantidos a filial.
              </p>
              <p className="text-[12px] font-light">
                Caso existam ambos Quantidade e Valor total, vale o que for atingido primeiro.
              </p>
            </div>
          </div>

          <Box className="flex flex-col gap-4 p-4">
            <Badge Icon={() => <FileBoxIcon strokeWidth={1} />} text={product.display_name || ''} />

            <div className="flex items-center gap-2">
              {cardCurrentLimits?.quantity_limit_for_display === null ? (
                <div className="grow">
                  <TextField label="Quantidade" value="Sem limite" disabled />
                </div>
              ) : (
                <div className="grow">
                  <MaskedTextField
                    label="Quantidade"
                    mask={customUnitMask(
                      unitForQuantity.find((unit) => unit.value === product.unit_for_quantity)?.symbol || '',
                    )}
                    value={cardCurrentLimits?.quantity_limit_for_display?.toString()}
                    disabled
                  />
                </div>
              )}

              <div>
                <p className="mt-4">Ou</p>
              </div>

              {cardCurrentLimits?.credit_limit_for_display === null ? (
                <div className="grow">
                  <TextField label="Valor total" value="Sem limite" disabled />
                </div>
              ) : (
                <div className="grow">
                  <TextField
                    label="Valor total"
                    value={formatCurrency(Number(cardCurrentLimits?.credit_limit_for_display))}
                    disabled
                  />
                </div>
              )}
            </div>

            <Box className="flex flex-col gap-2 p-4">
              <p className="text-sm font-medium">Limites (Visão cliente)</p>

              <div className="flex items-center gap-2">
                {cardCurrentLimits.quantity_limited_by_priced_credit_and_cap ? (
                  <MaskedTextField
                    label="Quantidade"
                    mask={customUnitMask(
                      unitForQuantity.find((unit) => unit.value === product.unit_for_quantity)?.symbol || '',
                    )}
                    value={cardCurrentLimits.quantity_limited_by_priced_credit_and_cap.toString()}
                    className="w-full"
                    disabled
                  />
                ) : (
                  <TextField label="Quantidade" value="Sem limite" disabled />
                )}

                <div>
                  <p className="mt-4">Ou</p>
                </div>

                <TextField
                  label="Valor total"
                  value={
                    cardCurrentLimits.credit_limited_by_priced_quantity_and_cap
                      ? formatCurrency(Number(cardCurrentLimits.credit_limited_by_priced_quantity_and_cap))
                      : 'Sem limite'
                  }
                  className="w-full"
                  disabled
                />
              </div>
            </Box>

            {cardDetails?.company?.kind === 'private_company' ? (
              <Box className="flex flex-col gap-2 p-4">
                <p className="text-sm font-medium">Limites da empresa (Privada)</p>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <TextField
                      label="Crédito garantido"
                      value={formatCurrency(Number(cardCurrentLimits.branch.granted_credit || 0))}
                      disabled
                    />

                    <p
                      className={cn('text-[12px] font-light italic', {
                        'text-danger-soft': shouldHighlightCreditField(
                          cardCurrentLimits.branch.remaining_credit,
                          cardCurrentLimits.credit_limit_for_display,
                        ),
                      })}
                    >
                      Restante:{' '}
                      {cardCurrentLimits.branch.remaining_credit
                        ? formatCurrency(Number(cardCurrentLimits.branch.remaining_credit || 0))
                        : 'Sem limite'}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <TextField
                      label="Valor limite do produto"
                      value={
                        cardCurrentLimits.branch.product_granted_credit
                          ? formatCurrency(Number(cardCurrentLimits.branch.product_granted_credit))
                          : 'Sem limite'
                      }
                      className={shouldHighlightCreditField(
                        cardCurrentLimits.branch.product_granted_credit,
                        cardCurrentLimits.credit_limit_for_display,
                      )}
                      disabled
                    />

                    <p
                      className={cn('text-[12px] font-light italic', {
                        'text-danger-soft': shouldHighlightCreditField(
                          cardCurrentLimits.branch.remaining_product_granted_credit,
                          cardCurrentLimits.credit_limit_for_display,
                        ),
                      })}
                    >
                      Restante:{' '}
                      {cardCurrentLimits.branch.remaining_product_granted_credit
                        ? formatCurrency(Number(cardCurrentLimits.branch.remaining_product_granted_credit || 0))
                        : 'Sem limite'}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <TextField
                      label="Valor limite da categoria"
                      value={
                        cardCurrentLimits.branch.product_category_granted_credit
                          ? formatCurrency(Number(cardCurrentLimits.branch.product_category_granted_credit))
                          : 'Sem limite'
                      }
                      className={shouldHighlightCreditField(
                        cardCurrentLimits.branch.product_category_granted_credit,
                        cardCurrentLimits.credit_limit_for_display,
                      )}
                      disabled
                    />

                    <p
                      className={cn('text-[12px] font-light italic', {
                        'text-danger-soft': shouldHighlightCreditField(
                          cardCurrentLimits.branch.remaining_product_category_granted_credit,
                          cardCurrentLimits.credit_limit_for_display,
                        ),
                      })}
                    >
                      Restante:{' '}
                      {cardCurrentLimits.branch.remaining_product_category_granted_credit
                        ? formatCurrency(Number(cardCurrentLimits.branch.remaining_product_category_granted_credit || 0))
                        : 'Sem limite'}
                    </p>
                  </div>
                </div>
              </Box>
            ) : (
              <Box className="flex flex-col gap-2 p-4">
                <p className="text-sm font-medium">Limites de distribuição de empenho (Pública)</p>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <TextField
                      label="Limite de crédito"
                      value={
                        cardCurrentLimits.government_allocation_distribution_limits.resulting_credit_limit
                          ? formatCurrency(Number(cardCurrentLimits.government_allocation_distribution_limits.resulting_credit_limit))
                          : 'Sem limite'
                      }
                      className={shouldHighlightCreditField(
                        cardCurrentLimits.branch.product_category_granted_credit,
                        cardCurrentLimits.credit_limit_for_display,
                      )}
                      disabled
                    />
                  </div>

                  <div className="space-y-1">
                    <TextField
                      label="Limite de quantidade"
                      value={
                        cardCurrentLimits.government_allocation_distribution_limits.resulting_quantity_limit
                          ? formatUnit(
                            formatDecimal(
                              cardCurrentLimits.government_allocation_distribution_limits.resulting_quantity_limit,
                              product.unit_for_quantity === 'item' ? 0 : 3
                            ),
                            product.unit_for_quantity
                          )
                          : 'Sem limite'
                      }
                      className={shouldHighlightCreditField(
                        cardCurrentLimits.branch.product_category_granted_credit,
                        cardCurrentLimits.credit_limit_for_display,
                      )}
                      disabled
                    />
                  </div>
                </div>
              </Box>
            )}

            <Box className="flex flex-col gap-2 p-4">
              <p className="text-sm font-medium">Limites do cartão</p>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <TextField
                    label="Valor limite por período"
                    value={
                      cardCurrentLimits.card.limit_per_period
                        ? formatCurrency(Number(cardCurrentLimits.card.limit_per_period))
                        : 'Sem limite'
                    }
                    className={shouldHighlightCreditField(
                      cardCurrentLimits.card.limit_per_period,
                      cardCurrentLimits.credit_limit_for_display,
                    )}
                    disabled
                  />

                  <p
                    className={cn('text-[12px] font-light italic', {
                      'text-danger-soft': shouldHighlightCreditField(
                        cardCurrentLimits.card.remaining_credit_limit_by_period,
                        cardCurrentLimits.credit_limit_for_display,
                      ),
                    })}
                  >
                    Restante:{' '}
                    {cardCurrentLimits.card.remaining_credit_limit_by_period
                      ? formatCurrency(Number(cardCurrentLimits.card.remaining_credit_limit_by_period || 0))
                      : 'Sem limite'}
                  </p>
                </div>

                <TextField
                  label="Valor limite por transação"
                  value={
                    cardCurrentLimits.card.limit_per_transaction
                      ? formatCurrency(Number(cardCurrentLimits.card.limit_per_transaction))
                      : 'Sem limite'
                  }
                  className={shouldHighlightCreditField(
                    cardCurrentLimits.card.limit_per_transaction,
                    cardCurrentLimits.credit_limit_for_display,
                  )}
                  disabled
                />

                <div className="space-y-1">
                  <TextField
                    label="Valor limite do produto por período"
                    value={
                      cardCurrentLimits.card.product_credit_limit_per_period
                        ? formatCurrency(Number(cardCurrentLimits.card.product_credit_limit_per_period))
                        : 'Sem limite'
                    }
                    className={shouldHighlightCreditField(
                      cardCurrentLimits.card.product_credit_limit_per_period,
                      cardCurrentLimits.credit_limit_for_display,
                    )}
                    disabled
                  />

                  <p
                    className={cn('text-[12px] font-light italic', {
                      'text-danger-soft': shouldHighlightCreditField(
                        cardCurrentLimits.card.product_remaining_credit_limit_per_period,
                        cardCurrentLimits.credit_limit_for_display,
                      ),
                    })}
                  >
                    Restante:{' '}
                    {cardCurrentLimits.card.product_remaining_credit_limit_per_period
                      ? formatCurrency(Number(cardCurrentLimits.card.product_remaining_credit_limit_per_period || 0))
                      : 'Sem limite'}
                  </p>
                </div>

                <TextField
                  label="Valor limite do produto por transação"
                  value={
                    cardCurrentLimits.card.product_credit_limit_per_transaction
                      ? formatCurrency(Number(cardCurrentLimits.card.product_credit_limit_per_transaction))
                      : 'Sem limite'
                  }
                  className={shouldHighlightCreditField(
                    cardCurrentLimits.card.product_credit_limit_per_transaction,
                    cardCurrentLimits.credit_limit_for_display,
                  )}
                  disabled
                />

                <div className="space-y-1">
                  {cardCurrentLimits.card.product_quantity_limit_per_period ? (
                    <MaskedTextField
                      label="Quantidade limite do produto por período"
                      mask={customUnitMask(
                        unitForQuantity.find((unit) => unit.value === product.unit_for_quantity)?.symbol || '',
                      )}
                      value={cardCurrentLimits.card.product_quantity_limit_per_period.toString()}
                      className={shouldHighlightQuantityField(
                        cardCurrentLimits.card.product_quantity_limit_per_period,
                        cardCurrentLimits.quantity_limit_for_display,
                      )}
                      disabled
                    />
                  ) : (
                    <TextField label="Quantidade limite do produto por período" value="Sem limite" disabled />
                  )}

                  <p
                    className={cn('text-[12px] font-light italic', {
                      'text-danger-soft': shouldHighlightQuantityField(
                        cardCurrentLimits.card.product_remaining_quantity_limit_per_period,
                        cardCurrentLimits.quantity_limit_for_display,
                      ),
                    })}
                  >
                    Restante:{' '}
                    {cardCurrentLimits.card.product_remaining_quantity_limit_per_period
                      ? cardCurrentLimits.card.product_remaining_quantity_limit_per_period.toString()
                      : 'Sem limite'}
                  </p>
                </div>

                {cardCurrentLimits.card.product_quantity_limit_per_transaction ? (
                  <MaskedTextField
                    label="Quantidade limite do produto por transação"
                    mask={customUnitMask(
                      unitForQuantity.find((unit) => unit.value === product.unit_for_quantity)?.symbol || '',
                    )}
                    value={cardCurrentLimits.card.product_quantity_limit_per_transaction.toString()}
                    className={shouldHighlightQuantityField(
                      cardCurrentLimits.card.product_quantity_limit_per_transaction,
                      cardCurrentLimits.quantity_limit_for_display,
                    )}
                    disabled
                  />
                ) : (
                  <TextField label="Quantidade limite do produto por transação" value="Sem limite" disabled />
                )}
              </div>
            </Box>
          </Box>
        </Box>
      )}

      {showLoading && (
        <Box className="h-[280px] flex items-center justify-center">
          <div className="w-6 h-6 border-4 border-dashed rounded-full animate-spin border-blue-800 mb-1 mr-1" />
        </Box>
      )}

      {showError && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangleIcon className="text-danger-soft" />

            <p className="text-danger-soft text-lg font-medium">Ocorreu um erro.</p>
          </div>

          {errorBase.length && errorBase.map((error) => <p className="text-danger-soft font-light">{error}</p>)}
        </div>
      )}
    </div>
  )
}
